/**
 * MobileWeekCarousel - Carrousel semaine pour mobile
 * Affiche les 7 jours horizontalement avec les événements miniaturisés
 * Permet de voir la semaine d'un coup et de naviguer rapidement
 */

import React, { memo, useRef, useMemo, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, ChevronDown, ChevronUp, Check, Clock } from 'lucide-react';

const DAYS_SHORT = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

// Catégories avec couleurs
const CATEGORIES = [
  { id: 'vital', color: '#EF4444' },
  { id: 'work', color: '#3B82F6' },
  { id: 'health', color: '#10B981' },
  { id: 'personal', color: '#F59E0B' },
  { id: 'social', color: '#8B5CF6' },
  { id: 'admin', color: '#06B6D4' },
];

const formatDateKey = (d) => {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const isToday = (d) => {
  const today = new Date();
  return d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
};

const timeToMinutes = (time) => {
  if (!time) return 0;
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};

/**
 * Mini event card for the carousel
 */
const MiniEventCard = memo(function MiniEventCard({ event, onTap, isCompact = false }) {
  const category = CATEGORIES.find(c => c.id === event.category) || CATEGORIES[3];
  const completedSubtasks = event.subTasks?.filter(st => st.completed).length || 0;
  const totalSubtasks = event.subTasks?.length || 0;
  const isCompleted = event.completed || (totalSubtasks > 0 && completedSubtasks === totalSubtasks);

  if (isCompact) {
    // Ultra compact: just a colored dot
    return (
      <motion.div
        className="w-full h-2 rounded-full mb-0.5"
        style={{ backgroundColor: category.color }}
        whileTap={{ scale: 0.95 }}
        onClick={() => onTap?.(event)}
      />
    );
  }

  return (
    <motion.div
      className={`w-full px-1.5 py-1 rounded mb-1 ${isCompleted ? 'opacity-50' : ''}`}
      style={{
        backgroundColor: `${category.color}15`,
        borderLeft: `2px solid ${category.color}`,
      }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onTap?.(event)}
    >
      <div className="flex items-center gap-1">
        <span
          className={`text-[9px] font-semibold truncate flex-1 ${isCompleted ? 'line-through' : ''}`}
          style={{ color: category.color }}
        >
          {event.title}
        </span>
        {totalSubtasks > 0 && (
          <span className="text-[8px] text-zinc-500">
            {completedSubtasks}/{totalSubtasks}
          </span>
        )}
      </div>
      <div className="text-[8px] text-zinc-500 font-mono">
        {event.start}
      </div>
    </motion.div>
  );
});

/**
 * Day column in the carousel
 */
const DayColumn = memo(function DayColumn({
  date,
  events,
  isSelected,
  onSelect,
  onEventTap,
  onAddTap,
  expandedDay,
  setExpandedDay,
}) {
  const dateKey = formatDateKey(date);
  const isTodayDate = isToday(date);
  const isExpanded = expandedDay === dateKey;

  // Sort events by start time
  const sortedEvents = useMemo(() => {
    return [...events].sort((a, b) => timeToMinutes(a.start) - timeToMinutes(b.start));
  }, [events]);

  // Calculate stats
  const totalDuration = sortedEvents.reduce((sum, e) => {
    return sum + (timeToMinutes(e.end) - timeToMinutes(e.start));
  }, 0);
  const completedCount = sortedEvents.filter(e =>
    e.completed || (e.subTasks?.length > 0 && e.subTasks.every(st => st.completed))
  ).length;

  const maxVisibleEvents = isExpanded ? sortedEvents.length : 3;
  const hasMoreEvents = sortedEvents.length > 3;

  return (
    <motion.div
      className={`flex-shrink-0 snap-center ${isExpanded ? 'w-[160px]' : 'w-[100px]'} transition-all duration-300`}
      animate={{ width: isExpanded ? 160 : 100 }}
    >
      {/* Day header */}
      <motion.div
        className={`text-center p-2 rounded-t-xl cursor-pointer ${
          isTodayDate
            ? 'bg-blue-600'
            : isSelected
              ? 'bg-zinc-700'
              : 'bg-zinc-800/50'
        }`}
        onClick={() => onSelect(date)}
        whileTap={{ scale: 0.98 }}
      >
        <div className={`text-[10px] uppercase font-bold tracking-wider ${
          isTodayDate ? 'text-blue-200' : 'text-zinc-400'
        }`}>
          {DAYS_SHORT[date.getDay()]}
        </div>
        <div className={`text-lg font-bold ${
          isTodayDate ? 'text-white' : 'text-zinc-200'
        }`}>
          {date.getDate()}
        </div>
        {/* Stats badge */}
        {events.length > 0 && (
          <div className="flex items-center justify-center gap-1 mt-1">
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${
              isTodayDate ? 'bg-blue-500 text-blue-100' : 'bg-zinc-700 text-zinc-300'
            }`}>
              {events.length} {completedCount > 0 && <Check className="w-2 h-2 inline" />}
            </span>
          </div>
        )}
      </motion.div>

      {/* Events container */}
      <div
        className={`bg-zinc-800/30 rounded-b-xl p-1.5 min-h-[120px] border border-zinc-700/30 ${
          isSelected ? 'border-zinc-600/50' : ''
        }`}
      >
        {sortedEvents.length === 0 ? (
          <motion.button
            className="w-full h-full min-h-[80px] flex flex-col items-center justify-center text-zinc-500 hover:text-zinc-400 hover:bg-zinc-700/30 rounded-lg transition-colors"
            onClick={() => onAddTap(date)}
            whileTap={{ scale: 0.95 }}
          >
            <Plus className="w-5 h-5 mb-1" />
            <span className="text-[10px]">Ajouter</span>
          </motion.button>
        ) : (
          <>
            <div className="space-y-0.5">
              {sortedEvents.slice(0, maxVisibleEvents).map((event) => (
                <MiniEventCard
                  key={event.id}
                  event={event}
                  onTap={onEventTap}
                  isCompact={!isExpanded && sortedEvents.length > 4}
                />
              ))}
            </div>

            {/* Expand/collapse button */}
            {hasMoreEvents && (
              <motion.button
                className="w-full mt-1 py-1 flex items-center justify-center gap-1 text-[10px] text-zinc-400 hover:text-zinc-300 hover:bg-zinc-700/30 rounded transition-colors"
                onClick={() => setExpandedDay(isExpanded ? null : dateKey)}
                whileTap={{ scale: 0.95 }}
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="w-3 h-3" />
                    <span>Réduire</span>
                  </>
                ) : (
                  <>
                    <span>+{sortedEvents.length - 3}</span>
                    <ChevronDown className="w-3 h-3" />
                  </>
                )}
              </motion.button>
            )}

            {/* Duration indicator */}
            <div className="flex items-center justify-center gap-1 mt-1.5 pt-1.5 border-t border-zinc-700/30">
              <Clock className="w-3 h-3 text-zinc-500" />
              <span className="text-[9px] text-zinc-500">
                {totalDuration >= 60
                  ? `${Math.floor(totalDuration / 60)}h${totalDuration % 60 > 0 ? totalDuration % 60 : ''}`
                  : `${totalDuration}min`
                }
              </span>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
});

/**
 * Main MobileWeekCarousel component
 */
export const MobileWeekCarousel = memo(function MobileWeekCarousel({
  weekDates,
  events,
  selectedDate,
  onSelectDate,
  onEventTap,
  onAddTap,
}) {
  const scrollRef = useRef(null);
  const [expandedDay, setExpandedDay] = useState(null);

  // Group events by date
  const eventsByDate = useMemo(() => {
    const grouped = {};
    weekDates.forEach(date => {
      const key = formatDateKey(date);
      grouped[key] = events.filter(e => e.date === key);
    });
    return grouped;
  }, [weekDates, events]);

  // Calculate week stats
  const weekStats = useMemo(() => {
    const total = events.filter(e => weekDates.some(d => formatDateKey(d) === e.date)).length;
    const completed = events.filter(e => {
      const inWeek = weekDates.some(d => formatDateKey(d) === e.date);
      const isComplete = e.completed || (e.subTasks?.length > 0 && e.subTasks.every(st => st.completed));
      return inWeek && isComplete;
    }).length;
    return { total, completed };
  }, [events, weekDates]);

  // Scroll to selected date
  useEffect(() => {
    if (scrollRef.current && selectedDate) {
      const selectedIndex = weekDates.findIndex(d => formatDateKey(d) === formatDateKey(selectedDate));
      if (selectedIndex >= 0) {
        const scrollAmount = selectedIndex * 108; // 100px width + 8px gap
        scrollRef.current.scrollTo({ left: scrollAmount - 50, behavior: 'smooth' });
      }
    }
  }, [selectedDate, weekDates]);

  return (
    <div className="bg-zinc-900/50 rounded-2xl p-3 border border-zinc-800/50">
      {/* Week header */}
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-zinc-300">Vue semaine</span>
          {weekStats.total > 0 && (
            <span className="text-xs px-2 py-0.5 bg-zinc-800 rounded-full text-zinc-400">
              {weekStats.completed}/{weekStats.total} fait{weekStats.completed > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="text-[10px] text-zinc-500 uppercase tracking-wider">
          Swipe
        </div>
      </div>

      {/* Scrollable carousel */}
      <div
        ref={scrollRef}
        className="flex gap-2 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {weekDates.map((date) => (
          <DayColumn
            key={formatDateKey(date)}
            date={date}
            events={eventsByDate[formatDateKey(date)] || []}
            isSelected={formatDateKey(date) === formatDateKey(selectedDate)}
            onSelect={onSelectDate}
            onEventTap={onEventTap}
            onAddTap={onAddTap}
            expandedDay={expandedDay}
            setExpandedDay={setExpandedDay}
          />
        ))}
      </div>

      {/* Scroll indicator dots */}
      <div className="flex justify-center gap-1 mt-2">
        {weekDates.map((date) => {
          const dateKey = formatDateKey(date);
          const hasEvents = eventsByDate[dateKey]?.length > 0;
          const isSelected = dateKey === formatDateKey(selectedDate);

          return (
            <div
              key={dateKey}
              className={`h-1 rounded-full transition-all duration-200 ${
                isSelected
                  ? 'w-4 bg-blue-500'
                  : hasEvents
                    ? 'w-1.5 bg-zinc-500'
                    : 'w-1 bg-zinc-700'
              }`}
            />
          );
        })}
      </div>
    </div>
  );
});

export default MobileWeekCarousel;
